import { Icon } from '@joor/design-system'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { HELP } from 'externalUrls'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { accessZendeskLoggedIn } from 'utils/sdks/zendesk'
import { clearStorage } from 'utils/storage'

import {
  allowManagePayments as allowManagePaymentsSelector,
  createIsIntegratedWithSelector,
  getUserId,
  isAccountTypeBrand,
  isLiteRetailer as isAccountTypeLiteRetailer,
  isEditInventorySettingsAllowed,
  isEditStylesRestricted,
  isLiteBrand,
  userCanTransact,
  userIsAdmin,
  userIsInternal,
  userIsLoggedInAsAdmin,
} from 'store/currentUser/selectors'

import {
  ADMIN_LOGOUT,
  LOGOUT,
  MY_SETTINGS,
  SSO_SETTINGS,
  SUBSCRIPTIONS,
  THIRD_PARTY_SETTINGS,
} from 'routes/paths'

import {
  ISettingsDropdown,
  ISettingsDropdownSelectors,
} from '../../navbarRevamp.types'
import DropdownMenuGroup from '../DropdownMenuGroup/DropdownMenuGroup'
import DropdownMenuTextLink from '../DropdownMenuTextLink/DropdownMenuTextLink'
import NavbarDropdown from '../NavbarDropdown/NavbarDropdown'
import Trigger from '../Trigger/Trigger'
import AdminSettings from './components/AdminSettings'
import BrandSettings from './components/BrandSettings'
import IntegrationsSettings from './components/IntegrationsSettings'
import RetailerSettings from './components/RetailerSettings'
import {
  useViewingUserManagementAccess,
  useViewingUserPermissionSettingsAccess,
} from './settingsDropdown.hooks'
import messages from 'features/Navbar/NavbarRevamp/constants/navbarRevamp.messages'
import { SettingsDropdownIds } from 'features/Navbar/NavbarRevamp/navbarRevamp.ids'
import { useNavbarLogout } from 'features/Navbar/hooks/useNavbarLogout'

const NavbarSettingsDropdownContainer = styled.div<ISettingsDropdown>`
  display: flex;

  .trigger > div {
    padding: 0 2px;

    > div {
      height: 24px;
    }
  }

  @media only screen and (max-width: 1024px) {
    display: ${({ isInsideDropdown }) => (isInsideDropdown ? 'flex' : 'none')};
  }
`

const SettingsMenuLabel = styled.span`
  margin-left: 4px;
  color: var(--color-secondary-800);
  font-size: var(--size-font-small);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  padding: 6px 0;
  margin-right: auto;
`

const SettingsIcon = styled.div<ISettingsDropdown>`
  position: relative;
  cursor: pointer;

  svg * {
    fill: ${({ isInsideDropdown }) =>
      isInsideDropdown ? 'var(--color-primary-600)' : 'white'};
  }

  @media only screen and (max-width: 1024px) {
    & > span {
      width: 20px;
      height: 20px;
      padding: 2px 0;

      svg {
        width: 20px;
        height: 20px;
      }
    }
  }
`

const CustomSettingsTrigger = ({ isInsideDropdown }: ISettingsDropdown) => (
  <Trigger
    label={
      <>
        <SettingsIcon isInsideDropdown={isInsideDropdown}>
          <Icon iconName="settings" variant="DEFAULT" type="white" plain />
        </SettingsIcon>
        <>
          {isInsideDropdown && (
            <SettingsMenuLabel>{messages.settings}</SettingsMenuLabel>
          )}
        </>
      </>
    }
    id={SettingsDropdownIds.SettingsTrigger}
    isInsideDropdown={isInsideDropdown}
  />
)

const NavbarSettingsDropdown = ({ isInsideDropdown }: ISettingsDropdown) => {
  const {
    allowEditInventorySettings,
    allowManagePayments,
    canTransact,
    isAdmin,
    isBrandAccount,
    isLiteRetailer,
    isIntegratedWithKellwood,
    isIntegratedWithRLM,
    isLiteBrandAccount,
    isLoggedInAsAdmin,
    restrictEditStyles,
    userId,
  } = useSelector<object, ISettingsDropdownSelectors>((state) => ({
    allowEditInventorySettings: isEditInventorySettingsAllowed(state),
    allowManagePayments: allowManagePaymentsSelector(state),
    canTransact: userCanTransact(state),
    isAdmin: userIsAdmin(state),
    isBrandAccount: isAccountTypeBrand(state),
    isLiteRetailer: isAccountTypeLiteRetailer(state),
    isIntegratedWithKellwood: createIsIntegratedWithSelector('Kellwood')(state),
    isIntegratedWithRLM: createIsIntegratedWithSelector('RLM')(state),
    isInternal: userIsInternal(state),
    isLiteBrandAccount: isLiteBrand(state),
    isLoggedInAsAdmin: userIsLoggedInAsAdmin(state),
    restrictEditStyles: isEditStylesRestricted(state),
    userId: getUserId(state),
  }))
  const {
    vntana,
    ssoManagement,
    brandPaymentsSettings,
    selfServePremiumFeatures,
  } = useFlags()
  const handleLogout = useNavbarLogout()
  const canSeeUserManagement = useViewingUserManagementAccess()
  const {
    canViewPermissions,
    canManagePermissions,
    canSeeNavbarOption,
    newPermissionsPage,
  } = useViewingUserPermissionSettingsAccess()

  return (
    <NavbarSettingsDropdownContainer isInsideDropdown={isInsideDropdown}>
      <NavbarDropdown
        testId={SettingsDropdownIds.SettingsTrigger}
        trigger={<CustomSettingsTrigger isInsideDropdown={isInsideDropdown} />}
        rightPosition
        isInsideDropdown={isInsideDropdown}
      >
        <DropdownMenuGroup>
          <DropdownMenuTextLink
            to={MY_SETTINGS}
            message={messages.mySettings}
            id={SettingsDropdownIds.SettingsOptionMySettings}
          />
          {selfServePremiumFeatures && isLiteRetailer && (
            <DropdownMenuTextLink
              to={SUBSCRIPTIONS}
              message={messages.premiumFeatures}
              id={SettingsDropdownIds.SettingsOptionMySettings}
              iconName={'unlock'}
            />
          )}
          {vntana && isBrandAccount && (
            <DropdownMenuTextLink
              to={THIRD_PARTY_SETTINGS}
              message={messages.thirdPartySettings}
              id={SettingsDropdownIds.SettingsOptionThirdParty}
            />
          )}
          <IntegrationsSettings />
          {ssoManagement && (
            <DropdownMenuTextLink
              to={SSO_SETTINGS}
              message={messages.ssoSettings}
              id={SettingsDropdownIds.SettingsOptionSSO}
            />
          )}
          <RetailerSettings />
          <BrandSettings
            isBrandAccount={isBrandAccount}
            isLiteBrandAccount={isLiteBrandAccount}
            restrictEditStyles={restrictEditStyles}
            allowManagePayments={allowManagePayments}
            brandPaymentsSettings={brandPaymentsSettings}
            canTransact={canTransact}
            canManagePermissions={canManagePermissions}
            canViewPermissions={canViewPermissions && canSeeNavbarOption}
            allowEditInventorySettings={allowEditInventorySettings}
            canSeeUserManagement={canSeeUserManagement}
            newPermissionsPage={newPermissionsPage}
          />
          <AdminSettings
            isAdmin={isAdmin}
            isLiteBrandAccount={isLiteBrandAccount}
            isIntegratedWithKellwood={isIntegratedWithKellwood}
            isIntegratedWithRLM={isIntegratedWithRLM}
            isBrandAccount={isBrandAccount}
          />
          <DropdownMenuTextLink
            to={accessZendeskLoggedIn(HELP)}
            message={messages.helpCenter}
            target="_blank"
            id={SettingsDropdownIds.SettingsOptionHelp}
          />
          {isLoggedInAsAdmin && (
            <DropdownMenuTextLink
              to={ADMIN_LOGOUT}
              message={messages.switchToAdmin}
              onClick={() => clearStorage(`${userId}`)}
              id={SettingsDropdownIds.SettingsOptionSwitchTo}
            />
          )}
          <DropdownMenuTextLink
            to={LOGOUT}
            message={messages.logout}
            onClick={handleLogout}
            id={SettingsDropdownIds.SettingsOptionLogout}
          />
        </DropdownMenuGroup>
      </NavbarDropdown>
    </NavbarSettingsDropdownContainer>
  )
}

export default NavbarSettingsDropdown
